import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import axios from 'axios'
import {
  Box,
  CircularProgress,
  ThemeProvider,
  createTheme,
} from "@mui/material";
import { CacheProvider } from "@emotion/react";
import { useDispatch } from "react-redux";
import { BrowserRouter as Router, useLocation } from "react-router-dom";
import { Navbar, RegionLanguagePill, Footer, changeLanguage } from "./components";
import { DEFAULT_CATEGORY } from "./constant/data";
import AppRoutes from "./route";
import { getAllCategories } from "./services/data/categories";
import { getAllCountries } from "./services/data/countries";
import { getCountryCategoryEvents } from "./services/data/events";
import { getAllPages } from "./services/data/pages";
import { getAllSlides } from "./services/data/slides";
import { getUserProfile } from "./services/profile";
import { getCategories } from "./store/slice/categories";
import { getCountries } from "./store/slice/countries";
import { getEvents } from "./store/slice/events";
import { updateLoader } from "./store/slice/loading";
import { getPages } from "./store/slice/pages";
import { getSlides } from "./store/slice/slides";
import { updateUserData, selectUser } from "./store/slice/user";
import { COLORS, fontFamilyFor } from "./theme";
import { rtlCache, ltrCache } from "./theme/rtlCache";
import { isRtl } from "./i18n";
import { Toaster } from "react-hot-toast";
import { initializeMixpanel } from "./utils";
import AlertPopup from "./components/AlertPopup";
initializeMixpanel();

// About/Pricing/How It Works/Contact Sales/Terms use their own lighter
// SubPageNav/SiteFooter (matching new-design's sub-page chrome) instead of
// the homepage's Navbar/Footer — see src/components/SubPageNav, SiteFooter.
const SUBPAGE_ROUTES = [
  "/about",
  "/pricing",
  "/how-it-works",
  "/contact-sales",
  "/terms-of-service",
  "/privacy-policy",
  "/refund-policy",
  "/models-influencers",
];

const AppContent = () => {
  const { pathname } = useLocation();
  const isSubPage = SUBPAGE_ROUTES.includes(pathname);

  // React Router doesn't reset scroll on navigation (unlike a full page
  // load), so following a link while scrolled down — e.g. a footer link —
  // otherwise lands on the new page at the same scroll offset.
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return (
    <>
      {!isSubPage && <Navbar />}
      <AppRoutes />
      {!isSubPage && !localStorage.getItem("isEmployee") && <Footer />}
    </>
  );
};

const App = () => {
  const token = localStorage.getItem("token");
    const [alertOpen, setAlertOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [countryApiName, setCountryApiName] = useState("");
  const dispatch = useDispatch();
  const { language } = useSelector(selectUser);
  const rtl = isRtl(language);
  const [loading, setLoading] = useState(true);
  const theme = createTheme({
    direction: rtl ? "rtl" : "ltr",
    typography: {
      fontFamily: fontFamilyFor(language),
    },
    palette: {
      primary: {
        light: COLORS.primary,
        main: COLORS.primary,
        dark: COLORS.primary,
      },
      secondary: {
        light: COLORS.secondary,
        main: COLORS.secondary,
        dark: COLORS.secondary,
      },
      black: {
        light: COLORS.black,
        main: COLORS.black,
        dark: COLORS.black,
      },
      white: {
        light: COLORS.white,
        main: COLORS.white,
        dark: COLORS.white,
      },
    },
  });
const detectAndSetCountryId = async (countriesList) => {
    console.group("🌍 [detectAndSetCountryId] START");
    
    // Default return object
    let result = { id: null, apiName: null };

    try {
      if (!countriesList?.length) {
        console.warn("⚠️ Countries list is empty");
        localStorage.setItem("countryId", JSON.stringify(null));
        console.groupEnd();
        return result;
      }

      console.log("📡 Fetching IP geolocation...");
      // const ipResponse = await axios.get("https://ip-api.com/json/");
      const ipResponse = await axios.get("https://api.country.is");
      const ipData = ipResponse.data;

      // Store the raw API name (e.g., "KW" or "United States") immediately
      result.apiName = ipData.country; 

      if (ipData.status !== "success") {
        console.warn("⚠️ ip-api failed");
        localStorage.setItem("countryId", JSON.stringify(null));
        console.groupEnd();
        return result;
      }

      const countryMap = { kw: "Kuwait" };
      const normalizedApiCountry = countryMap[ipData.country?.toLowerCase()] || ipData.country;

      const matchedCountry = countriesList.find((c) => {
        const dbName = c?.translation?.en?.name;
        return dbName?.toLowerCase().trim() === normalizedApiCountry?.toLowerCase().trim();
      });

      if (matchedCountry) {
        console.log("✅ MATCH FOUND:", matchedCountry);
        localStorage.setItem("countryId", JSON.stringify(matchedCountry.id));
        result.id = matchedCountry.id;
      } else {
        console.warn("❌ NO MATCH in DB");
        localStorage.setItem("countryId", JSON.stringify(null));
      }

    } catch (error) {
      console.error("❌ Detection Error:", error);
    }
    
    console.groupEnd();
    return result;
  };

const getData = async () => {
  console.group("🚀 [getData] START");

  let language = localStorage.getItem("language");
  let category = localStorage.getItem("category");
  console.log("📦 localStorage on entry →", { language, category });

  changeLanguage(language);

  if (!category?.length) {
    localStorage.setItem("category", JSON.stringify(DEFAULT_CATEGORY));
    console.log("📝 category was empty, set to DEFAULT_CATEGORY:", DEFAULT_CATEGORY);
  }
  if (!language?.length) {
    localStorage.setItem("language", "en");
    dispatch(updateUserData({ language: "en" }));
    console.log("📝 language was empty, defaulted to 'en'");
  }

  console.log("📡 Fetching countries from server...");
  const countriesResponse = await getAllCountries();
  const countriesList = countriesResponse?.data?.data || [];
  console.log("✅ Countries fetched:", countriesList.length, "entries");

  // Honor a region the user already picked (via the region/language pill)
  // across reloads — only fall back to IP-based geolocation for a
  // first-time visitor who hasn't made a choice yet. Previously this ran
  // IP detection unconditionally on every load and overwrote the stored
  // selection, so switching region never survived a refresh.
  let storedCountryId = null;
  try {
    storedCountryId = JSON.parse(localStorage.getItem("countryId"));
  } catch {
    storedCountryId = localStorage.getItem("countryId");
  }
  const storedCountryIsValid =
    storedCountryId != null &&
    countriesList.some((c) => c.id === Number(storedCountryId));

  let resolvedCountryId;
  let detectedApiName = "Your Region";

  if (storedCountryIsValid) {
    resolvedCountryId = Number(storedCountryId);
    console.log("💾 Using previously selected countryId:", resolvedCountryId);
  } else {
    console.log("🔍 No valid stored region — running IP detection...");
    const detectionResult = await detectAndSetCountryId(countriesList);
    resolvedCountryId = detectionResult.id ?? null;
    detectedApiName = detectionResult.apiName || "Your Region";
  }

  // Save API name to state for the alert
  setCountryApiName(detectedApiName);

  console.log("🗺️ resolvedCountryId (used for events fetch):", resolvedCountryId);

  dispatch(updateUserData({ countryId: resolvedCountryId }));
  console.log("🗃️ Dispatched updateUserData with countryId:", resolvedCountryId);

  console.log("📡 Fetching slides, events, categories, pages in parallel...");
  const slides = getAllSlides();
  const events = getCountryCategoryEvents({
    page: 1,
    number: 12,
    country: resolvedCountryId,
    category: 0,
  });
  const categories = getAllCategories({ page: 1, number: 10 });
  const pages = getAllPages();

  Promise.all([slides, events, categories, pages])
    .then(([slidesResponse, eventResponse, categoriesResponse, pagesResponse]) => {
      console.log("✅ All parallel fetches resolved");
      
      // Check events data structure (safety check)
      const eventsData = eventResponse?.data?.data?.data || [];
      console.log("  events:", eventsData.length, "items");

      dispatch(getSlides(slidesResponse?.data?.data));
      dispatch(getCountries(countriesList));
      dispatch(getEvents(eventResponse?.data?.data));
      dispatch(getCategories(categoriesResponse?.data?.data?.data));
      dispatch(getPages(pagesResponse.data.data));

      console.log("✅ All dispatches done — setting loading false");
      setLoading(false);
      dispatch(updateLoader({ loader: false }));

      console.groupEnd();
    })
    .catch((err) => {
      console.error("❌ Promise.all FAILED:", err?.message, err);
      setLoading(false);
      console.groupEnd();
    });
};
//  console.log(detectedId)
  const getUserData = async (token) => {
    getUserProfile(token)
      .then((res) => {
        dispatch(updateUserData({ ...res.data.data, token }));
        getData();
      })
      .catch((err) => {
        localStorage.removeItem("token");
        getData();
      });
  };
  
  useEffect(() => {
    if (!!token?.length) {
      getUserData(token);
    } else {
      getData();
    }
  }, []);
  return (
    <CacheProvider value={rtl ? rtlCache : ltrCache}>
      <Router>
        <ThemeProvider theme={theme}>
          <Toaster position="top-right" reverseOrder={false} />
            <AlertPopup
            open={alertOpen}
            onClose={() => setAlertOpen(false)}
            title="No Events Found"
            message={alertMessage}
          />

          <Box
            sx={{
              width: "100%",
              maxWidth: "1440px",
              minHeight: "100vh",
              display: "flex",
              flexDirection: "column",
              m: "auto",
              boxSizing: "border-box",
              justifyContent: loading ? "center" : "initial",
              alignItems: loading ? "center" : "initial",
            }}
          >
            {!loading ? (
              <>
                <AppContent />
                <RegionLanguagePill />
              </>
            ) : (
              <CircularProgress size={100} color="secondary" />
            )}
          </Box>
        </ThemeProvider>
      </Router>
    </CacheProvider>
  );
};

export default App;
